/**
 * @license
 * Copyright (c) 2017, Barclays Africa Technology, All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file
 */
import { ActionReducer } from '@ngrx/store';
import { createSelector } from 'reselect';

// import { Info } from '../model/info';

/**
 * Every reducer module's default export is the reducer function itself. In
 * addition, each module should export a type or interface that describes
 * the state of the reducer plus any selector functions. The `* as`
 * notation packages up all of the exports into a single object.
 */
import * as from from './reducers/reducer';
import * as fromExperience from './reducers/experience.reducer';
import * as fromThresholdUser from './reducers/user.reducer';


export interface ThresholdState {
    THR: from.ThresholdState;
    THRAppExperience: fromExperience.ThresholdExperienceState;
    THRUser: fromThresholdUser.ThresholdUserState;
}

/**
 * Because metareducers take a reducer function and return a new reducer,
 * we can use our compose helper to chain them together. Here we are
 * using combineReducers to make our top level reducer, and then
 * wrapping that in storeLogger. Remember that compose applies
 * the result from right to left.
 */
export const reducers = {
  THR: from.reducer,
  THRAppExperience: fromExperience.reducer,
  THRUser: fromThresholdUser.reducer
}

/**
 * Every reducer module exports selector functions, however child reducers
 * have no knowledge of the overall state tree. To make them useable, we
 * need to make new selectors that wrap them.
 *
 * The createSelector function from the reselect library creates
 * very efficient selectors that are memoized and only recompute when arguments change.
 * The created selectors can also be composed together to select different
 * pieces of state.
 */

/**
 * Just like with the projects selectors, we also have to compose the search
 * reducer's and collection reducer's selectors.
 */
export const getThresholdState = (state: ThresholdState) => state.THR

export const getApplicationTitle = createSelector(getThresholdState, from.getApplicationTitle);
export const getBannerClass = createSelector(getThresholdState, from.getBannerClass);
export const getBannerBackground = createSelector(getThresholdState, from.getBackgroundBanner);
export const getBannerTitle = createSelector(getThresholdState, from.getBannerTitle);
export const getBannerCaption = createSelector(getThresholdState, from.getBannerCaption);
export const getShowContextNav = createSelector(getThresholdState, from.getShowContextNav);
export const getContextNav = createSelector(getThresholdState, from.getContextNav);
export const getShowProfileIcon = createSelector(getThresholdState, from.getShowProfileIcon);
export const getBannerAlign = createSelector(getThresholdState, from.getBannerAlign);
export const getShowBanner = createSelector(getThresholdState, from.getShowBanner);
export const getShowMenu = createSelector(getThresholdState, from.getShowMenu);
export const getShowMainMenu = createSelector(getThresholdState, from.getShowMainMenu);
export const getMainMenu = createSelector(getThresholdState, from.getMainMenu);
export const getBreadcrumb = createSelector(getThresholdState, from.getBreadcrumb);
export const getColor = createSelector(getThresholdState, from.getColor);
export const getVariant = createSelector(getThresholdState, from.getVariant);
export const getSize = createSelector(getThresholdState, from.getSize);
export const getShowSearchControl = createSelector(getThresholdState, from.getShowSearchControl);
export const getShowSearchBar = createSelector(getThresholdState, from.getShowSearchBar);
export const getDynamicMenu = createSelector(getThresholdState, from.getDynamicNav);
export const getShowDynamicSmallMenuWithNoChildren = createSelector(getThresholdState, from.getShowDynamicSmallMenuWithNoChildren);
export const getLocalizationDefaultLang = createSelector(getThresholdState, from.getLocalizationDefaultLang);
export const getLocalizationPackages = createSelector(getThresholdState, from.getLocalizationPackages);
export const getLocalizationLoading = createSelector(getThresholdState, from.getLocalizationLoading);
export const getActivityFeature = createSelector(getThresholdState, from.getActivityFeature);
export const getShowHeaderMasthead = createSelector(getThresholdState, from.getShowHeaderMasthead);
export const getAllowExternalNavigation = createSelector(getThresholdState, from.getAllowExternalNavigation);
export const getContainerClass = createSelector(getThresholdState, from.getContainerClass);


export const getThresholdExperienceState = (state: ThresholdState) => state.THRAppExperience
export const getExperience = createSelector(getThresholdExperienceState, fromExperience.getExperiences);

export const getThresholdUserState = (state: ThresholdState) => state.THRUser
export const getUserProfiles = createSelector(getThresholdUserState, fromThresholdUser.getUserProfiles);


// WEBPACK FOOTER //
// node_modules/angular2-template-loader!./~/angular-router-loader/src!./src/app/threshold/store/store.ts